// Standup — the landing view of the Unified Inbox.
//
// Structure (top → bottom):
//   TopNav (sticky)
//   Hero — greeting + 3 metrics
//   Section 01 · The Pile — Needs You (decide-cards)
//   Section 02 · One tap away — Drafts Ready (empty until Phase D)
//   Section 03 · Waking up — Snoozed threads (Phase 5a)
//   Section 04 · Circle back — Follow-ups
//   Section 05 · For the record — Auto-handled
//   Footer band — keyboard shortcuts
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopNav } from '@/components/TopNav';
import type { BrandFilter } from '@/components/TopNav';
import { Hero } from '@/components/standup/Hero';
import { DecideCard } from '@/components/standup/DecideCard';
import { FollowupRow } from '@/components/standup/FollowupRow';
import { EmptyBucket } from '@/components/standup/EmptyBucket';
import { FooterBand } from '@/components/standup/FooterBand';
import { BulkActionBar } from '@/components/standup/BulkActionBar';
import { ShortcutCheatSheet } from '@/components/standup/ShortcutCheatSheet';
import { NewTicketModal } from '@/components/standup/NewTicketModal';
import {
  ChannelHealthBanner,
  ChannelHealthWidget,
} from '@/components/standup/ChannelHealthWidget';
import { Tag } from '@/components/brand/Tag';
import { useConversations, type ConversationRow } from '@/lib/hooks/useConversations';
import { useSelection } from '@/lib/hooks/useSelection';
import { useBulkStatus } from '@/lib/hooks/useBulkStatus';
import { useStandupShortcuts, cycleFocus } from '@/lib/hooks/useStandupShortcuts';
import { useEnsureSummaries } from '@/lib/hooks/useEnsureSummaries';
import {
  useEnsureIntents,
  FALLBACK_INTENT_META,
  type IntentEntry,
  type IntentMeta,
} from '@/lib/hooks/useEnsureIntents';
import {
  toDecideCardData,
  toFollowupRowData,
  formatStandupTimestamp,
  displayName,
} from '@/lib/display';
import { useAuth } from '@/auth/AuthProvider';

// Polish #7 — how many resolved threads we'll render in section 05 before
// cutting off. Set generously since grouping makes the list easy to scan;
// if Kimi needs more she can search or filter. Tuning dial kept in one
// place so the summary-prefetch cap and the render slice stay in sync.
const RESOLVED_MAX_VISIBLE = 24;

type TagVariantForChannel = 'ch-gmail' | 'ch-etsy' | 'ch-amzn' | 'ch-shop' | 'ch-ig';
const CHANNEL_TAG: Record<string, TagVariantForChannel> = {
  gmail: 'ch-gmail',
  etsy_md: 'ch-etsy',
  etsy_hk: 'ch-etsy',
  amazon: 'ch-amzn',
  shopify_md: 'ch-shop',
  shopify_brokerage: 'ch-shop',
};
const CHANNEL_LABEL: Record<string, string> = {
  gmail: 'Gmail',
  etsy_md: 'Etsy',
  etsy_hk: 'Etsy',
  amazon: 'Amazon',
  shopify_md: 'Shopify',
  shopify_brokerage: 'Shopify',
};
/**
 * Polish #7 — group resolved rows into Today / Yesterday / Earlier this week
 * using the local timezone. Returns groups in render order; empty groups are
 * omitted. Uses `status_changed_at` (which is stamped on every transition)
 * as the canonical "resolved on" timestamp.
 */
type ResolvedGroup = {
  key: 'today' | 'yesterday' | 'earlier';
  label: string;
  rows: ConversationRow[];
};

function groupResolvedByDay(rows: ConversationRow[]): ResolvedGroup[] {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;

  const today: ConversationRow[] = [];
  const yesterday: ConversationRow[] = [];
  const earlier: ConversationRow[] = [];

  for (const r of rows) {
    const t = new Date(r.status_changed_at).getTime();
    if (Number.isNaN(t)) {
      earlier.push(r);
      continue;
    }
    if (t >= startOfToday) today.push(r);
    else if (t >= startOfYesterday) yesterday.push(r);
    else earlier.push(r);
  }

  const out: ResolvedGroup[] = [];
  if (today.length) out.push({ key: 'today', label: 'Today', rows: today });
  if (yesterday.length) out.push({ key: 'yesterday', label: 'Yesterday', rows: yesterday });
  if (earlier.length) out.push({ key: 'earlier', label: 'Earlier this week', rows: earlier });
  return out;
}

/**
 * Short human label for a snoozed_until timestamp relative to now.
 * "waking up" once the wake cron is about to run; "in 2h" / "in 3d" otherwise.
 */
function formatWakeIn(iso: string | null): string {
  if (!iso) return 'soon';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'waking up';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return minutes <= 1 ? 'in <1m' : `in ${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `in ${hours}h`;
  const days = Math.round(hours / 24);
  return `in ${days}d`;
}
export function StandupPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [brand, setBrand] = useState<BrandFilter>('all');
  const { data, isLoading, error } = useConversations(brand);
  const firstName = useMemo(() => {
    const local = (user?.email ?? 'Kimi').split('@')[0] ?? 'Kimi';
    return local.charAt(0).toUpperCase() + local.slice(1).split(/[._-]/)[0];
  }, [user]);
  const timestamp = useMemo(() => formatStandupTimestamp(new Date()), []);
  const handleOpen = (id: string) => {
    // Navigate to the Command Center thread view.
    navigate(`/thread/${id}`);
  };
  const needsYou = data?.needsYou ?? [];
  const followUps = data?.followUps ?? [];
  const autoHandled = data?.autoHandled ?? [];
  const snoozed = data?.snoozed ?? [];
  const hiddenSpam = data?.hiddenSpam ?? [];

  // Polish #7 — the Hero metric still reads as "already handled today" not
  // "already handled this week," so derive the 24h subset locally. Keeps
  // Hero's "came in / handled / need" arithmetic meaningful even though
  // the section 05 list now spans 7 days.
  const autoHandledToday = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return autoHandled.filter((c) => {
      const t = new Date(c.status_changed_at).getTime();
      return Number.isFinite(t) && t >= cutoff;
    });
  }, [autoHandled]);

  // Phase 6i — pull AI summaries for every visible conversation across the
  // four buckets. One round-trip instead of per-card, keeps the summary
  // generation batch-friendly on the backend.
  const visibleIds = useMemo(() => {
    const ids = new Set<string>();
    needsYou.slice(0, 12).forEach((c) => ids.add(c.id));
    snoozed.slice(0, 10).forEach((c) => ids.add(c.id));
    followUps.slice(0, 10).forEach((c) => ids.add(c.id));
    // Polish #7 — we now render the resolved bucket grouped by date so bump
    // the summary prefetch cap to cover all visible groups.
    autoHandled.slice(0, RESOLVED_MAX_VISIBLE).forEach((c) => ids.add(c.id));
    return Array.from(ids);
  }, [needsYou, snoozed, followUps, autoHandled]);
  const summariesQuery = useEnsureSummaries(visibleIds);
  const summaries = summariesQuery.data?.summaries ?? {};
  const summaryFor = (id: string) => summaries[id] ?? null;

  // Phase 9 — pull intent classifications for the same visible set. The hook
  // is enabled-on-non-empty so single round-trip on bucket load. Failures are
  // silent: the chip just doesn't render.
  const intentsQuery = useEnsureIntents(visibleIds);
  const intents: Record<string, IntentEntry> = intentsQuery.data?.intents ?? {};
  const intentMeta: IntentMeta = intentsQuery.data?.meta ?? FALLBACK_INTENT_META;
  const intentChipFor = (id: string) => {
    const entry = intents[id];
    if (!entry || entry.intent === 'generic') return null;
    const meta = intentMeta[entry.intent];
    if (!meta) return null;
    return {
      intent: entry.intent,
      label: meta.label,
      chipClass: meta.chip,
      evidence: entry.evidence_phrase,
    };
  };
  // Polish #2 — spam is fully auto-hidden from the Pile. The inline "Show N
  // hidden" toggle is gone; the only way to inspect caught threads is the
  // dedicated /spam audit route, which pulls a wider sweep. This keeps the
  // main queue free of any spam hint while preserving a quiet escape hatch.

  // Phase 6b — multi-select + bulk actions. Only The Pile + revealed spam
  // participate in selection; Snoozed / Circle back / For the record are
  // not selectable here (they have their own lifecycles and a bulk action
  // there has fuzzier UX value).
  const selection = useSelection();
  const bulkStatus = useBulkStatus();
  const runBulk = (
    status: 'resolved' | 'awaiting' | 'snoozed' | 'spam',
    opts?: { snoozeMinutes?: number },
  ) => {
    if (selection.count === 0) return;
    const ids = Array.from(selection.ids);
    const snoozed_until =
      status === 'snoozed' && opts?.snoozeMinutes
        ? new Date(Date.now() + opts.snoozeMinutes * 60_000).toISOString()
        : undefined;
    bulkStatus.mutate(
      { ids, status, snoozed_until },
      {
        onSuccess: () => selection.clear(),
      },
    );
  };
  // Click-anywhere-to-select: once any card is selected, subsequent clicks
  // on a card body toggle its selection instead of opening the thread.
  // The "Open thread" button still opens regardless.
  const selectionActive = selection.count > 0;

  // Phase 6h — keyboard shortcuts (?, J, K, /, Esc). `useStandupShortcuts`
  // is a no-op while focus is inside an input/textarea so Composer typing
  // is never interrupted.
  const [cheatOpen, setCheatOpen] = useState(false);
  // Polish #1 — new-ticket composer modal state. `c` shortcut + the
  // full-width bar below the hero both open it.
  const [composeOpen, setComposeOpen] = useState(false);
  useStandupShortcuts({
    onNext: () => cycleFocus('.decide-card', 1),
    onPrev: () => cycleFocus('.decide-card', -1),
    onCheatSheet: () => setCheatOpen(true),
    onCompose: () => setComposeOpen(true),
    onEscape: () => {
      setCheatOpen(false);
      setComposeOpen(false);
      if (selection.count > 0) selection.clear();
    },
  });

  // Phase 6g — '/' on Standup navigates to the Search page. The shortcut
  // hook dispatches `md-inbox:focus-search`; we listen here so routes
  // other than Standup can opt out (Center.tsx does not mount this).
  useEffect(() => {
    const handler = () => navigate('/search');
    window.addEventListener('md-inbox:focus-search', handler);
    return () => window.removeEventListener('md-inbox:focus-search', handler);
  }, [navigate]);
  return (
    <>
      <TopNav
        view="standup"
        onViewChange={(v) => {
          if (v === 'standup') return; // already here
          if (v === 'center') {
            // Jump to the first Needs You thread; fall back to most recent
            // conversation the user has in any bucket, else stay put.
            const firstId =
              needsYou[0]?.id ??
              followUps[0]?.id ??
              snoozed[0]?.id ??
              autoHandled[0]?.id ??
              null;
            if (firstId) navigate(`/thread/${firstId}`);
            return;
          }
          // 'pipe' / 'settings' not implemented yet — ignore click.
        }}
        brand={brand}
        onBrandChange={setBrand}
        onSearchClick={() => navigate('/search')}
        onAnalyticsClick={() => navigate('/analytics')}
      />
      {/* Polish #20 — red banner if any channel is in the error state.
          Sits above the hero so a broken Gmail poll can't hide behind
          bucket counts that still look healthy from cached data. */}
      <ChannelHealthBanner />
      <Hero
        firstName={firstName}
        timestamp={timestamp}
        needsYou={needsYou.length}
        draftsReady={0}
        autoHandled={autoHandledToday.length}
      />
      {/* Polish #20 — channel health dashboard widget. Always rendered so
          Kimi can scan the status of Gmail, Etsy, and ShipStation at a
          glance without hunting in admin settings. */}
      <div className="wrap" style={{ marginTop: 24 }}>
        <ChannelHealthWidget />
      </div>
      {/* Polish #1 — full-width compose bar. Click anywhere on the bar OR
          press `c` from anywhere on Standup to open the new-ticket modal. */}
      <div className="wrap">
        <button
          type="button"
          className="new-ticket-bar"
          onClick={() => setComposeOpen(true)}
          aria-label="Start a new conversation"
        >
          <span className="new-ticket-bar-left">
            <span className="new-ticket-bar-glyph" aria-hidden="true">+</span>
            <span className="new-ticket-bar-text">
              <span className="new-ticket-bar-title">Start a new conversation</span>
              <span className="new-ticket-bar-sub">
                Email a customer directly, from MD or HarperKellee.
              </span>
            </span>
          </span>
          <span className="new-ticket-bar-shortcut" aria-hidden="true">C</span>
        </button>
      </div>
      {/* 01 · Needs You */}
      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <div className="lede">
              <div className="eyebrow">01 · The Pile</div>
              <h2 className="display h2">
                {needsYou.length > 0 ? needsYou.length : 'Nothing'}{' '}
                <span className="accent">need{needsYou.length === 1 ? 's' : 's'} you</span>
                {needsYou.length === 0 ? ' right now' : '.'}
              </h2>
              <p>
                I can't answer these on my own — they're judgement calls. Tap a card to jump into
                Command Center.
              </p>
            </div>
          </div>
          {isLoading ? (
            <EmptyBucket
              headline="Loading your <em>pile</em>…"
              body="Pulling conversations from Supabase. This should take under a second."
            />
          ) : error ? (
            <EmptyBucket
              headline="I couldn't reach the inbox"
              body={`Supabase returned an error: ${(error as Error).message}. Try reloading — if it keeps happening, check the backend deploy status.`}
            />
          ) : needsYou.length === 0 ? (
            <EmptyBucket
              headline="You're caught up on <em>decisions</em>."
              body="Everything in the pile is either drafted, waiting on someone else, or already handled. Nice."
            />
          ) : (
            <>
              <div className="decide-grid">
                {needsYou.slice(0, 12).map((c) => (
                  <DecideCard
                    key={c.id}
                    data={toDecideCardData(c, data?.excerpts.get(c.id), summaryFor(c.id), intentChipFor(c.id))}
                    onOpen={handleOpen}
                    selection={{
                      selected: selection.has(c.id),
                      onToggle: selection.toggle,
                      selectionActive,
                    }}
                  />
                ))}
              </div>
              {/* Polish #2 — deliberately minimal: one sentence, far right, linking
                  to /spam. Only shows when the matcher caught something this load.
                  Kimi sees that the filter's working without seeing the spam itself. */}
              {hiddenSpam.length > 0 ? (
                <div
                  style={{
                    marginTop: 18,
                    color: 'hsl(var(--ink) / 0.45)',
                    fontSize: 12,
                    textAlign: 'right',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => navigate('/spam')}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'inherit',
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                      cursor: 'pointer',
                      padding: 0,
                      font: 'inherit',
                    }}
                  >
                    Spam filter caught {hiddenSpam.length} today · review
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>
      {/* 02 · Drafts Ready (empty until Phase D) */}
      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <div className="lede">
              <div className="eyebrow">02 · One tap away</div>
              <h2 className="display h2">
                <span className="accent">Draft review</span> — coming soon.
              </h2>
              <p>
                Once Haiku is wired up (next phase), I'll draft replies to routine questions in
                your voice. You'll be able to scan, tweak, or send from right here.
              </p>
            </div>
          </div>
          <EmptyBucket
            headline="No drafts <em>yet</em>."
            body="The drafter ships in the next build session. Until then, open threads in Command Center and reply manually."
          />
        </div>
      </section>
      {/* 03 · Waking up (Snoozed) */}
      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <div className="lede">
              <div className="eyebrow">03 · Waking up</div>
              <h2 className="display h2">
                {snoozed.length === 0 ? 'Nothing ' : `${snoozed.length} ${snoozed.length === 1 ? 'thread is' : 'threads are'} `}
                <span className="accent">snoozed</span>
                {snoozed.length === 0 ? ' right now.' : '.'}
              </h2>
              <p>
                Set aside for future you. Soonest wake time first — each one flips back to
                Awaiting on its own when the timer hits.
              </p>
            </div>
          </div>
          {snoozed.length === 0 ? (
            <EmptyBucket
              headline="Nothing is on <em>snooze</em>."
              body="When you hit Z on a thread or pick Snoozed from the status pill, it'll land here until the wake time."
            />
          ) : (
            <div className="auto-card">
              <div className="rows">
                {snoozed.slice(0, 10).map((c) => {
                  const subject = (c.subject ?? '').trim();
                  const s = summaryFor(c.id);
                  const headline = s?.summary ?? (subject.length > 0 ? subject : 'Snoozed thread');
                  return (
                    <button
                      type="button"
                      className="auto-row"
                      key={c.id}
                      onClick={() => handleOpen(c.id)}
                      style={{
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        width: '100%',
                      }}
                    >
                      {/* Summary-first; falls back to subject when no AI summary yet. */}
                      <span className="what" style={{ fontWeight: 600 }}>
                        {headline}
                      </span>
                      <Tag variant={c.brand === 'md' ? 'brand-md' : 'brand-hk'}>
                        {c.brand === 'md' ? 'MD' : 'HK'}
                      </Tag>
                      <Tag variant={CHANNEL_TAG[c.channel]}>{CHANNEL_LABEL[c.channel]}</Tag>
                      <span className="who">
                        {displayName(c.customer_email, null, c.subject)}
                        <span style={{ color: 'hsl(var(--ink) / 0.5)', marginLeft: 8 }}>
                          · {formatWakeIn(c.snoozed_until)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>
      {/* 04 · Follow-ups */}
      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <div className="lede">
              <div className="eyebrow">04 · Circle back</div>
              <h2 className="display h2">
                {followUps.length === 0 ? 'No one is ' : `${followUps.length} ${followUps.length === 1 ? 'person is' : 'people are'} `}
                <span className="accent">waiting on you</span>.
              </h2>
              <p>
                Threads where you were the last to say "I'll get back to you." I watch these so you
                don't have to remember.
              </p>
            </div>
          </div>
          {followUps.length === 0 ? (
            <EmptyBucket
              headline="No <em>open promises</em>."
              body="Every outbound reply has either been answered or is still within its 2-day window."
            />
          ) : (
            <div className="followups">
              {followUps.slice(0, 10).map((c) => (
                <FollowupRow key={c.id} data={toFollowupRowData(c, summaryFor(c.id), intentChipFor(c.id))} onOpen={handleOpen} />
              ))}
            </div>
          )}
        </div>
      </section>
      {/* 05 · Auto-handled — Polish #7 widened to 7 days and grouped by
          Today / Yesterday / Earlier this week. */}
      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <div className="lede">
              <div className="eyebrow">05 · For the record</div>
              <h2 className="display h2">
                {autoHandled.length === 0 ? 'Nothing ' : `${autoHandled.length} `}
                <span className="accent">resolved</span>
                {autoHandled.length === 0 ? ' this week yet.' : ' this week.'}
              </h2>
              <p>
                No action needed, just a running record of what moved to "resolved." Grouped by day
                so the stuff Kimi just finished sits at the top.
              </p>
            </div>
          </div>
          {autoHandled.length === 0 ? (
            <EmptyBucket
              headline="Nothing to report <em>yet</em>."
              body="Once threads get marked resolved, they'll show up here grouped by day."
            />
          ) : (
            (() => {
              const visible = autoHandled.slice(0, RESOLVED_MAX_VISIBLE);
              const groups = groupResolvedByDay(visible);
              const hiddenCount = autoHandled.length - visible.length;
              return (
                <div className="auto-card">
                  {groups.map((group) => (
                    <div key={group.key} className="resolved-group">
                      <div className="resolved-group-head">
                        <span className="resolved-group-label">{group.label}</span>
                        <span className="resolved-group-count">
                          {group.rows.length}{' '}
                          {group.rows.length === 1 ? 'thread' : 'threads'}
                        </span>
                      </div>
                      <div className="rows">
                        {group.rows.map((c) => {
                          const subject = (c.subject ?? '').trim();
                          const s = summaryFor(c.id);
                          const headline =
                            s?.summary ?? (subject.length > 0 ? subject : 'Resolved thread');
                          return (
                            <button
                              type="button"
                              className="auto-row"
                              key={c.id}
                              onClick={() => handleOpen(c.id)}
                              style={{
                                textAlign: 'left',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                width: '100%',
                              }}
                            >
                              {/* Summary-first; falls back to subject when no AI summary yet. */}
                              <span className="what" style={{ fontWeight: 600 }}>
                                {headline}
                              </span>
                              <Tag variant={c.brand === 'md' ? 'brand-md' : 'brand-hk'}>
                                {c.brand === 'md' ? 'MD' : 'HK'}
                              </Tag>
                              <Tag variant={CHANNEL_TAG[c.channel]}>
                                {CHANNEL_LABEL[c.channel]}
                              </Tag>
                              <span className="who">
                                {displayName(c.customer_email, null, c.subject)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {hiddenCount > 0 ? (
                    <div className="resolved-overflow">
                      {hiddenCount} more {hiddenCount === 1 ? 'thread' : 'threads'} resolved this
                      week, not shown.
                    </div>
                  ) : null}
                </div>
              );
            })()
          )}
        </div>
      </section>
      <FooterBand />
      {/* Phase 6b — floats above FooterBand when count > 0. */}
      <BulkActionBar
        count={selection.count}
        busy={bulkStatus.isPending}
        onResolve={() => runBulk('resolved')}
        onSnooze={(minutes) => runBulk('snoozed', { snoozeMinutes: minutes })}
        onFollowUp={() => runBulk('awaiting')}
        onSpam={() => runBulk('spam')}
        onCancel={() => selection.clear()}
        // Polish #24 — let Kimi escalate to every visible Pile card in one
        // click. Counts The Pile (capped at 12 in the render slice). Polish #2
        // dropped spam from the picker since spam is no longer visible here.
        selectAllCount={Math.min(needsYou.length, 12)}
        onSelectAll={() => {
          const ids: string[] = [];
          needsYou.slice(0, 12).forEach((c) => ids.push(c.id));
          selection.select(ids);
        }}
      />
      {/* Phase 6h — cheat sheet triggered by ?. */}
      <ShortcutCheatSheet open={cheatOpen} onClose={() => setCheatOpen(false)} />
      <NewTicketModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={(conversationId) => navigate(`/thread/${conversationId}`)}
        defaultBrand={brand === 'hk' ? 'hk' : 'md'}
      />
    </>
  );
}
